import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Chip. Compact pill for filters/tags. Smaller than Badge.
 * The `toggle` variant uses a NEUTRAL outline (never a colored status border);
 * its active state fills with primary. Optional trailing remove (x) action.
 */
const chipVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-so-12 font-bold leading-none transition-[background-color,color,border-color] duration-150',
  {
    variants: {
      variant: {
        default: 'bg-sober-muted text-sober-secondary',
        blue: 'bg-sober-primary-bg text-sober-primary',
        toggle: 'border border-sober-outline bg-sober-surface text-sober-text',
      },
      active: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'toggle',
        active: true,
        className: 'border-transparent bg-sober-primary-fg text-white',
      },
    ],
    defaultVariants: { variant: 'default', active: false },
  }
);

export interface ChipProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof chipVariants> {
  onRemove?: () => void;
}

export const Chip = React.forwardRef<HTMLSpanElement, ChipProps>(
  ({ className, variant, active, onRemove, children, ...props }, ref) => (
    <span ref={ref} className={cn(chipVariants({ variant, active }), className)} {...props}>
      {children}
      {onRemove ? (
        <button
          type="button"
          aria-label="Remover"
          onClick={onRemove}
          className="-mr-0.5 inline-flex size-3.5 shrink-0 items-center justify-center rounded-full leading-none opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sober-primary"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      ) : null}
    </span>
  )
);
Chip.displayName = 'Chip';

export { chipVariants };
